import type { Action, Contact, Initiative, Task, Unit, UserProfile } from '../types';

/**
 * Reading references on a record: the canonical Person field first, the legacy Contact field as a fallback (a record
 * written by an older client before the server has derived the Person field). Pure helpers — no network.
 */
export type ContactToPerson = (contactId: string) => string | null;

export const contactToPersonFrom = (contacts: Contact[]): ContactToPerson => {
  const map = new Map<string, string>();
  for (const c of contacts) if (c.personId) map.set(c.id, c.personId);
  return (id) => map.get(id) ?? null;
};

export function ownerPersonOf(a: Pick<Action, 'ownerPersonId' | 'ownerContactId'>, toPerson: ContactToPerson): string | null {
  if (a.ownerPersonId) return a.ownerPersonId;
  return a.ownerContactId ? toPerson(a.ownerContactId) : null;
}
export function participantPersonsOf(a: Pick<Action, 'participantPersonIds' | 'participantContactIds'>, toPerson: ContactToPerson): string[] {
  if (Array.isArray(a.participantPersonIds)) return a.participantPersonIds;
  return [...new Set((a.participantContactIds ?? []).map(toPerson).filter((p): p is string => !!p))];
}
export function assigneePersonsOf(t: Pick<Task, 'assignedPersonIds' | 'assignedContactIds'>, toPerson: ContactToPerson): string[] {
  if (Array.isArray(t.assignedPersonIds)) return t.assignedPersonIds;
  return [...new Set((t.assignedContactIds ?? []).map(toPerson).filter((p): p is string => !!p))];
}
export function leadPersonsOf(i: Pick<Initiative, 'leadPersonIds' | 'leadContactIds'>, toPerson: ContactToPerson): string[] {
  if (Array.isArray(i.leadPersonIds)) return i.leadPersonIds;
  return [...new Set((i.leadContactIds ?? []).map(toPerson).filter((p): p is string => !!p))];
}
export function commissionerPersonOf(u: Pick<Unit, 'commissionerPersonId' | 'commissionerContactId'>, toPerson: ContactToPerson): string | null {
  if (u.commissionerPersonId) return u.commissionerPersonId;
  return u.commissionerContactId ? toPerson(u.commissionerContactId) : null;
}

/** Is this record "mine" by identity: the login's Person (canonical) or, failing that, its roster Contact. */
export function isMe(profile: Pick<UserProfile, 'personId' | 'contactId'>, personId: string | null | undefined, contactId?: string | null): boolean {
  if (profile.personId && personId && profile.personId === personId) return true;
  return !!profile.contactId && !!contactId && profile.contactId === contactId;
}
